// Pure wave-grouping for the SIM-1140 pipeline DAG page: snapshot, projectDetail and now in, a
// per-wave list of display-ready nodes out. No I/O, no React. The wave model is CONNECTOR →
// BRONZE-SNAPSHOT/SILVER-LATEST → SILVER → GOLD → DIAMOND → ARCHIVE, sourced from connectors,
// `bronze|silver`-prefixed snapshot datasets, and transform or export definitions by layer.
// SIM-1350 pairs bronze and silver-latest into one node per (connectorPrefix, baseName), keeping
// both BQ table names on it, id `bronze-silver-latest:${prefix}|${baseName}`.

import type {
  PipelineStatus,
  RunCadence,
  TableRunStatus,
  TransformRunStatus,
} from "@Simplicity-Education/core";
import { computePipelineStatus } from "@Simplicity-Education/core";
import type { ExportDefinitionLatestRun } from "../configTypes";
import { scheduleToCadenceMinutes } from "./pipeline-status";

export type Wave = "connector" | "bronze-silver-latest" | "silver" | "gold" | "diamond" | "archive";

export interface BronzeSilverSideMeta {
  datasetId: string;
  tableId: string;
  /** Epoch ms when this side was last modified, or null when unknown. */
  lastModifiedMs: number | null;
  numRows: number | null;
}

export interface WaveNode {
  /** Unique within the page; encodes wave + entityId so React keys are stable. */
  id: string;
  wave: Wave;
  label: string;
  /** Underlying entity id (connector id / TD id / ED id / synthetic dataset|table key). */
  entityId: string;
  /**
   * Schedule names when known (e.g. ["hourly", "daily"]). A single table can
   * appear in multiple schedules; SIM-1350 surfaces them all so the card can
   * render one badge per cadence. Empty/undefined when not applicable
   * (connectors, TDs without schedule binding).
   */
  cadences?: string[];
  /** Current row count when known. Undefined when not applicable (e.g. connector). */
  rowCount?: number;
  status: PipelineStatus;
  /**
   * Other WaveNode ids this node consumes, from `TransformDefinitionInput.dependencies` for
   * silver-canon / gold / diamond. Empty for entities whose source links are not in the contract
   * yet (connector, bronze-silver-latest, archive). Used to render per-table dependency edges and
   * to trace cascades (SIM-1141).
   */
  dependsOnIds: string[];
  /**
   * SIM-1350: for `bronze-silver-latest` nodes, both sides of the pair are
   * carried so the drawer can show the underlying real BQ table names. Either
   * side may be null when only one side is present in the snapshot.
   */
  bronzeMeta?: BronzeSilverSideMeta | null;
  silverLatestMeta?: BronzeSilverSideMeta | null;
  /**
   * SIM-1350: `false` marks the node inactive — the UI hides inactive children by default, with a
   * wave-header toggle to reveal them — and undefined is treated as active. Derivation per source:
   * connectors from `archivedAt == null`, transform- and export-derived nodes from `enabled`, and
   * bronze/silver pairs `true` when any connector schedule references the base name.
   */
  active?: boolean;
}

export interface PipelineGraph {
  waves: Record<Wave, WaveNode[]>;
  /** Waves that the page should render. Omits empty waves AND diamond unconditionally for slice 1. */
  visibleWaves: Wave[];
}

// ─── Narrow input types ──────────────────────────────────────────────────────
//
// Subsets of the platform contract types in `admin/{snapshot,projects}/*.contract.ts`, kept narrow
// so this function does not couple to the full contract and so spec fixtures stay readable.
// Wire-shaped fields derive from configTypes.ts so they cannot drift (SIM-2733).

interface ConnectorScheduleEntry {
  name: string;
}

export interface ConnectorInput {
  id: string;
  name: string;
  prefix: string;
  vars: {
    schedules?: Record<string, ConnectorScheduleEntry[]>;
    silverDataset?: string;
    bronzeDataset?: string;
  };
  /** SIM-1350: archived connectors render as inactive. */
  archivedAt?: string | null;
}

interface BqTableInput {
  tableId: string;
  numRows?: number | null;
  lastModifiedTime?: string | null;
}

interface DatasetInput {
  datasetId: string;
  tables: BqTableInput[];
}

export interface SnapshotInput {
  datasets: DatasetInput[];
  /**
   * The wire payload nests the capture time under `project` (see
   * apps/platform/.../snapshot/snapshot.contract.ts). Optional because the
   * fixtures in this repo predate it; when absent, staleness falls back to
   * measuring against `now`, which is the pre-SIM-2576 behaviour.
   */
  project?: { reportedAt?: string | Date | null } | null;
}

interface TransformDependencyInput {
  sourceDataset: string;
  sourceTable: string;
}

export interface TransformDefinitionInput {
  id: string;
  name: string;
  /**
   * Wire-shaped (`configTypes.ts` says `string`). The server validates with
   * `transformLayerSchema`, so only the three known layers arrive today;
   * unrecognized values are handled inconsistently here — SIM-2741.
   */
  layer: string;
  outputDataset: string;
  outputTable: string;
  dependencies?: TransformDependencyInput[];
  /**
   * SIM-1350: `enabled === false` marks an inactive transform. Inactive
   * transforms render as muted children and are hidden behind a per-wave
   * "show inactive" toggle. Optional in this narrow type so older fixtures
   * that don't supply it default to active.
   */
  enabled?: boolean;
}

export interface ExportDefinitionInput {
  id: string;
  name: string;
  cadence: string;
  latestRun: Pick<ExportDefinitionLatestRun, "status" | "endedAt" | "rowCount"> | null;
  /** SIM-1350: see TransformDefinitionInput.enabled. */
  enabled?: boolean;
}

export interface ProjectDetailInput {
  connectors: ConnectorInput[];
  transformDefinitions: TransformDefinitionInput[];
  exportDefinitions: ExportDefinitionInput[];
}

/**
 * SIM-2845 / SIM-2913: the run records the DAG's verdicts come from. Null when the run-status
 * request failed or has not resolved, and rendered as `unknown` rather than falling back to a
 * BigQuery timestamp — that fallback IS the defect this closes, since a table timestamp cannot
 * distinguish "the ingest ran" from "something touched the table".
 */
interface RunStatusInput {
  tables: TableRunStatus[];
  transforms: TransformRunStatus[];
}

export interface BuildPipelineGraphInput {
  snapshot: SnapshotInput;
  projectDetail: ProjectDetailInput;
  runStatus: RunStatusInput | null;
  now: Date;
}

/**
 * Verdicts keyed the two ways the DAG asks for them. Tables use `connectorId|<lowercased base
 * name>`, because a bronze/silver pair is identified by its owning connector plus base name — GEO
 * Network's four PowerSchool connectors each carry a table called `attendance`, so the connector is
 * load-bearing in the key. Transforms key on the output object.
 */
interface RunStatusIndex {
  byConnectorTable: Map<string, TableRunStatus>;
  byTransformOutput: Map<string, TransformRunStatus>;
}

function indexRunStatus(runStatus: RunStatusInput | null): RunStatusIndex {
  const byConnectorTable = new Map<string, TableRunStatus>();
  const byTransformOutput = new Map<string, TransformRunStatus>();
  for (const t of runStatus?.tables ?? []) {
    byConnectorTable.set(`${t.connectorId}|${t.table.trim().toLowerCase()}`, t);
  }
  for (const t of runStatus?.transforms ?? []) {
    byTransformOutput.set(`${t.outputDataset}|${t.outputTable}`, t);
  }
  return { byConnectorTable, byTransformOutput };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const EMPTY_WAVES: Record<Wave, WaveNode[]> = {
  connector: [],
  "bronze-silver-latest": [],
  silver: [],
  gold: [],
  diamond: [],
  archive: [],
};

/**
 * Strip a connector prefix off a tableId; returns the base name. Exported for
 * the drawer (SIM-1350) so it can resolve a pair entityId back to the BQ
 * tables on either side without duplicating the parsing convention.
 */
export function tableBaseName(tableId: string): string {
  // tableIds look like `ps__students__snapshots`, `ps__students__latest`, or
  // `students` (no prefix). Drop the leading `prefix__` if present and any
  // trailing `__snapshots` / `__latest` suffix.
  const parts = tableId.split("__");
  if (parts.length >= 3) return parts[1] ?? tableId;
  if (parts.length === 2) return parts[0] ?? tableId;
  return tableId;
}

/**
 * Build a map: lowercased table base name → sorted unique cadences in minutes.
 * SIM-1350 follow-up: a single table can appear in multiple schedules
 * (e.g. `hourly` + `daily` for attendance); surface all of them so the
 * card can render each as its own badge.
 */
function buildTableCadenceMap(connectors: ConnectorInput[]): Map<string, number[]> {
  const map = new Map<string, Set<number>>();
  for (const connector of connectors) {
    const schedules = connector.vars.schedules ?? {};
    for (const [scheduleName, entries] of Object.entries(schedules)) {
      const cadenceMinutes = scheduleToCadenceMinutes(scheduleName);
      if (cadenceMinutes == null) continue;
      for (const entry of entries) {
        const key = entry.name.toLowerCase();
        const existing = map.get(key);
        if (existing == null) map.set(key, new Set([cadenceMinutes]));
        else existing.add(cadenceMinutes);
      }
    }
  }
  // Freeze each entry to a sorted ascending array (smallest cadence first).
  const out = new Map<string, number[]>();
  for (const [k, v] of map)
    out.set(
      k,
      [...v].sort((a, b) => a - b),
    );
  return out;
}

/**
 * Extract the connector prefix from a bronze_X / silver_X dataset name. Tolerates
 * double-underscore variants (`bronze__X`) and naked `bronze` / `silver` datasets (the Paramount
 * convention — returns ""), and null when it is not a bronze/silver dataset at all. Exported for
 * the drawer (SIM-1350), which resolves a pair entityId back to its constituent BQ tables.
 */
export function bronzeOrSilverPrefix(datasetId: string): string | null {
  const m = datasetId.match(/^(?:bronze|silver)(?:_+(.+))?$/i);
  if (m == null) return null;
  return m[1] ?? "";
}

/** Side of a bronze/silver dataset. Exported for drawer use (SIM-1350). */
export function bronzeOrSilverSide(datasetId: string): "bronze" | "silver" | null {
  if (/^bronze(?:_|$)/i.test(datasetId)) return "bronze";
  if (/^silver(?:_|$)/i.test(datasetId)) return "silver";
  return null;
}

function buildConnectorNodes(connectors: ConnectorInput[]): WaveNode[] {
  return connectors.map((c) => ({
    id: `connector:${c.id}`,
    wave: "connector" as const,
    label: c.name,
    entityId: c.id,
    status: "fresh" as const,
    dependsOnIds: [],
    active: c.archivedAt == null,
  }));
}

/**
 * Synthetic node id for a bronze-silver-latest pair.
 *
 * Format: `bronze-silver-latest:${prefix}|${baseName}`. Both sides of the pair
 * (`bronze_X.X__Y__snapshots` and `silver_X.X__Y__latest`) collapse to this id.
 */
function bronzeSilverPairId(prefix: string, baseName: string): string {
  return `bronze-silver-latest:${prefix}|${baseName}`;
}

/**
 * Build a `${dataset}|${table}` to WaveNode.id lookup for anything downstream might depend on, used
 * to resolve a transform's `dependencies[]` into a concrete node id. SIM-1350: bronze-silver-latest
 * entries map to the paired id whichever side is referenced, so a gold TD reading
 * `silver_ps.ps__students__latest` resolves to the same node as one reading bronze.
 */
function buildOutputLookup(
  transformDefinitions: TransformDefinitionInput[],
  snapshot: SnapshotInput,
): Map<string, string> {
  const lookup = new Map<string, string>();
  for (const td of transformDefinitions) {
    const key = `${td.outputDataset}|${td.outputTable}`;
    const wave: Wave =
      td.layer === "silver-canon" ? "silver" : td.layer === "gold" ? "gold" : "diamond";
    lookup.set(key, `${wave}:${td.id}`);
  }
  for (const dataset of snapshot.datasets) {
    const prefix = bronzeOrSilverPrefix(dataset.datasetId);
    if (prefix == null) continue;
    for (const table of dataset.tables) {
      const key = `${dataset.datasetId}|${table.tableId}`;
      // TD outputs win when they collide with the raw BQ pattern (silver-canon
      // outputs that incidentally live in a silver_* dataset).
      if (lookup.has(key)) continue;
      const base = tableBaseName(table.tableId);
      lookup.set(key, bronzeSilverPairId(prefix, base));
    }
  }
  return lookup;
}

interface PairAccumulator {
  prefix: string;
  baseName: string;
  bronze: BronzeSilverSideMeta | null;
  silverLatest: BronzeSilverSideMeta | null;
}

function parseLastModifiedMs(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * `endedAt` is an ISO string on the wire (plain `apiFetch` + JSON.parse, no Date revival), unlike
 * BQ's epoch-ms-as-string `lastModifiedTime` — hence a separate helper. The finite guard is
 * load-bearing, not defensive: an unparseable timestamp must become null (→ `never`) rather than
 * NaN, which compares as fresh and reports a stale export as healthy (SIM-2733).
 */
function parseIsoMs(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
}

function recordPairSide(
  pairs: Map<string, PairAccumulator>,
  pairKey: string,
  prefix: string,
  baseName: string,
  side: "bronze" | "silver",
  meta: BronzeSilverSideMeta,
): void {
  const existing = pairs.get(pairKey);
  if (existing == null) {
    pairs.set(pairKey, {
      prefix,
      baseName,
      bronze: side === "bronze" ? meta : null,
      silverLatest: side === "silver" ? meta : null,
    });
    return;
  }
  if (side === "bronze" && existing.bronze == null) existing.bronze = meta;
  if (side === "silver" && existing.silverLatest == null) existing.silverLatest = meta;
}

function collectPairs(snapshot: SnapshotInput): Map<string, PairAccumulator> {
  const pairs = new Map<string, PairAccumulator>();
  for (const dataset of snapshot.datasets) {
    const side = bronzeOrSilverSide(dataset.datasetId);
    const prefix = bronzeOrSilverPrefix(dataset.datasetId);
    if (side == null || prefix == null) continue;
    for (const table of dataset.tables) {
      const baseName = tableBaseName(table.tableId);
      const pairKey = `${prefix}|${baseName}`;
      const meta: BronzeSilverSideMeta = {
        datasetId: dataset.datasetId,
        tableId: table.tableId,
        lastModifiedMs: parseLastModifiedMs(table.lastModifiedTime),
        numRows: table.numRows ?? null,
      };
      recordPairSide(pairs, pairKey, prefix, baseName, side, meta);
    }
  }
  return pairs;
}

// SIM-2845: `pairAnchorLastModifiedMs` was deleted here. It picked the silver
// (else bronze) BigQuery `lastModifiedTime` as the pair's freshness anchor —
// the derivation this slice removes. `lastModifiedTime` advances on any DML, so
// it could not distinguish "the ingest ran" from "a cleanup overwrote the
// table", and silver `__latest` is CREATE-OR-REPLACE, so it read fresh forever
// whenever the sync died while the transform kept running.

function pairRowCount(pair: PairAccumulator): number | undefined {
  return pair.silverLatest?.numRows ?? pair.bronze?.numRows ?? undefined;
}

/**
 * Build a lookup from lowercased table base name to the connector ids that schedule it. SIM-1350: a
 * single base name can be scheduled by several connectors, so a pair depends on all of them.
 * Schedules are the source of truth here — the earlier prefix-based lookup assumed datasets follow
 * `bronze__{connector.prefix}`, which held for only about half of clients.
 */
/**
 * Loose connector shape used by the owning-connector lookup. Both
 * `ConnectorInput` and the drawer's `ProjectDetailResponse['connectors']`
 * shape match this — the lookup itself doesn't care about anything else.
 */
type ConnectorLike = {
  id: string;
  prefix: string;
  vars: Record<string, unknown>;
  archivedAt?: string | null;
};

interface ConnectorVarsView {
  schedules?: Record<string, ConnectorScheduleEntry[]>;
  bronzeDataset?: string;
  silverDataset?: string;
}

/**
 * Datasets a connector "owns" (writes to). Reads `connector.vars.bronzeDataset` / `silverDataset`
 * when declared, else falls back to the convention — including both single- and double-underscore
 * variants, since both appear in production (`bronze_ps` and `bronze__ps`).
 */
function ownedDatasetIds(c: ConnectorLike): string[] {
  const v = c.vars as ConnectorVarsView;
  const out: string[] = [];
  if (typeof v.bronzeDataset === "string" && v.bronzeDataset !== "") out.push(v.bronzeDataset);
  if (typeof v.silverDataset === "string" && v.silverDataset !== "") out.push(v.silverDataset);
  if (out.length === 0) {
    out.push(
      `bronze__${c.prefix}`,
      `silver__${c.prefix}`,
      `bronze_${c.prefix}`,
      `silver_${c.prefix}`,
    );
  }
  return out;
}

function connectorSchedulesTable(c: ConnectorLike, baseName: string): boolean {
  const needle = baseName.toLowerCase();
  const schedules = (c.vars as ConnectorVarsView).schedules ?? {};
  for (const entries of Object.values(schedules)) {
    if (entries.some((e) => e.name.toLowerCase() === needle)) return true;
  }
  return false;
}

/**
 * Find the connector(s) owning a bronze/silver pair. SIM-1350: dataset ownership is the PRIMARY
 * signal — a connector owns the datasets named in `vars.bronzeDataset` and `vars.silverDataset`,
 * with a prefix-convention fallback — and schedules only narrow a shared dataset. That is what
 * returns exactly one connector for GEO Network's four; a schedules-only lookup returned four.
 */
function findOwningConnectorIds(
  datasetIds: ReadonlyArray<string | null | undefined>,
  baseName: string,
  connectors: ReadonlyArray<ConnectorLike>,
): string[] {
  const datasets = new Set(datasetIds.filter((d): d is string => d != null && d !== ""));
  if (datasets.size === 0) return [];
  const owners: string[] = [];
  for (const c of connectors) {
    if (c.archivedAt != null) continue;
    if (ownedDatasetIds(c).some((d) => datasets.has(d))) owners.push(c.id);
  }
  if (owners.length <= 1) return owners;
  // Multiple owners → narrow by schedules.
  const narrowed = connectors
    .filter((c) => owners.includes(c.id) && connectorSchedulesTable(c, baseName))
    .map((c) => c.id);
  return narrowed.length > 0 ? narrowed : owners;
}

/**
 * Drawer-facing wrapper: returns owning connectors as { id, prefix } pairs
 * so the UI can render names and pick an Edit target.
 */
export function findConnectorsForPair(
  datasetIds: ReadonlyArray<string | null | undefined>,
  baseName: string,
  connectors: ReadonlyArray<ConnectorLike>,
): { id: string; prefix: string }[] {
  const ids = new Set(findOwningConnectorIds(datasetIds, baseName, connectors));
  return connectors.filter((c) => ids.has(c.id)).map((c) => ({ id: c.id, prefix: c.prefix }));
}

function pairToWaveNode(
  pairKey: string,
  pair: PairAccumulator,
  cadenceByTableBase: Map<string, number[]>,
  connectors: ConnectorInput[],
  runIndex: RunStatusIndex,
): WaveNode {
  const cadenceMinutesList = cadenceByTableBase.get(pair.baseName.toLowerCase()) ?? [];
  // SIM-1350 round 6: dataset ownership is the primary signal for the
  // connector edge. See findOwningConnectorIds for the full reasoning;
  // briefly, each connector's vars.bronzeDataset / silverDataset (or the
  // convention fallback) determines which datasets it "owns", and shared
  // datasets are disambiguated by schedules.
  const ownerIds = findOwningConnectorIds(
    [pair.bronze?.datasetId, pair.silverLatest?.datasetId],
    pair.baseName,
    connectors,
  );
  // SIM-2845: the verdict is the server's, keyed on (owning connector, table). It used to be
  // derived from the pair's BigQuery `lastModifiedTime`, which is why the DAG and the config rows
  // could disagree about one table — a BQ timestamp advances on any DML and says nothing about
  // whether the ingest ran. Two writers can produce a silver table, so two lookups: a connector
  // sync recorded as a `TableRun`, or a silver-canon transform recorded as a `QueryRun` keyed on
  // its output. `unknown` is the honest answer for an orphan with no config entry.
  const run =
    ownerIds
      .map((id) => runIndex.byConnectorTable.get(`${id}|${pair.baseName.trim().toLowerCase()}`))
      .find((r) => r != null)?.dot ??
    (pair.silverLatest
      ? runIndex.byTransformOutput.get(
          `${pair.silverLatest.datasetId}|${pair.silverLatest.tableId}`,
        )?.dot
      : undefined);

  return {
    id: bronzeSilverPairId(pair.prefix, pair.baseName),
    wave: "bronze-silver-latest",
    label: pair.baseName,
    entityId: pairKey,
    cadences: cadenceMinutesList.map(cadenceMinutesToLabel),
    rowCount: pairRowCount(pair),
    status: run ?? "unknown",
    dependsOnIds: ownerIds.map((id) => `connector:${id}`),
    bronzeMeta: pair.bronze,
    silverLatestMeta: pair.silverLatest,
    // "Active" = referenced by some connector schedule. Orphaned tables that
    // exist in BQ but no longer have a schedule become inactive.
    active: cadenceMinutesList.length > 0,
  };
}

function buildBronzeSilverLatestNodes(
  snapshot: SnapshotInput,
  cadenceByTableBase: Map<string, number[]>,
  connectors: ConnectorInput[],
  runIndex: RunStatusIndex,
): WaveNode[] {
  const pairs = collectPairs(snapshot);
  const nodes: WaveNode[] = [];
  for (const [pairKey, pair] of pairs.entries()) {
    nodes.push(pairToWaveNode(pairKey, pair, cadenceByTableBase, connectors, runIndex));
  }
  // Alphabetical by base name for stable rendering (bronze-silver-latest has
  // no intra-column deps to topologically sort by).
  nodes.sort((a, b) => a.label.localeCompare(b.label));
  return nodes;
}

function cadenceMinutesToLabel(minutes: number): string {
  if (minutes === 60) return "hourly";
  if (minutes === 1440) return "daily";
  if (minutes === 10080) return "weekly";
  return `${minutes}m`;
}

/** Index snapshot tables for O(1) (datasetId, tableId) lookups. */
function indexSnapshotTables(snapshot: SnapshotInput): Map<string, BqTableInput> {
  const map = new Map<string, BqTableInput>();
  for (const dataset of snapshot.datasets) {
    for (const table of dataset.tables) {
      map.set(`${dataset.datasetId}|${table.tableId}`, table);
    }
  }
  return map;
}

function buildTransformDefinitionNodes(
  transformDefinitions: TransformDefinitionInput[],
  snapshotIndex: Map<string, BqTableInput>,
  outputLookup: Map<string, string>,
  layer: "silver-canon" | "gold" | "diamond",
  wave: Wave,
  runIndex: RunStatusIndex,
): WaveNode[] {
  const nodes: WaveNode[] = [];
  for (const td of transformDefinitions) {
    if (td.layer !== layer) continue;
    const outputTable = snapshotIndex.get(`${td.outputDataset}|${td.outputTable}`);
    // SIM-2845: previously these were computed with a null cadence, which meant
    // EVERY transform node rendered `unknown` regardless of its BQ timestamp —
    // the node read the timestamp and then discarded it. `QueryRun` carries a
    // real execution record and the server already reduces it per definition,
    // so the node now shows the same verdict as the Transforms tab.
    const status =
      runIndex.byTransformOutput.get(`${td.outputDataset}|${td.outputTable}`)?.dot ?? "unknown";
    const dependsOnIds: string[] = [];
    for (const dep of td.dependencies ?? []) {
      const sourceId = outputLookup.get(`${dep.sourceDataset}|${dep.sourceTable}`);
      if (sourceId != null && sourceId !== `${wave}:${td.id}`) dependsOnIds.push(sourceId);
    }
    nodes.push({
      id: `${wave}:${td.id}`,
      wave,
      label: td.name,
      entityId: td.id,
      active: td.enabled !== false,
      rowCount: outputTable?.numRows ?? undefined,
      status,
      dependsOnIds,
    });
  }
  return nodes;
}

/**
 * Map an export cadence's interval back onto a `RunCadence` so the shared window
 * logic applies. Returns an empty list for an interval that matches no known
 * cadence, which `computePipelineStatus` reads as "nothing is due".
 */
function exportCadence(cadenceMinutes: number | null): RunCadence[] {
  if (cadenceMinutes === 60) return ["hourly"];
  if (cadenceMinutes === 1440) return ["daily"];
  if (cadenceMinutes === 10080) return ["weekly"];
  return [];
}

function buildArchiveNodes(exportDefinitions: ExportDefinitionInput[], now: Date): WaveNode[] {
  const nowMs = now.getTime();
  return exportDefinitions.map((ed) => {
    const cadenceMinutes = scheduleToCadenceMinutes(ed.cadence);
    const lastModifiedMs = parseIsoMs(ed.latestRun?.endedAt);
    // Export nodes keep their own source — `ExportRun.endedAt` is already a real
    // run record, not BQ metadata, so SIM-2845's complaint never applied here.
    // What changes is the vocabulary: one `computePipelineStatus` rather than a
    // second local verdict. The cadence label maps onto a RunCadence so the
    // shared window logic applies; an unrecognised export cadence yields no
    // window, which correctly means "nothing is due".
    const cadences = exportCadence(cadenceMinutes);
    // Two cases answered with `unknown` rather than a guess: an unrecognised export cadence, where
    // something plainly writes the export but we cannot say when it is due (`unscheduled` would
    // falsely claim nothing writes it); and a run whose `endedAt` will not parse, which is an
    // instrument problem rather than evidence it never ran (`never` would overclaim). SIM-2733
    // requires only that neither reads as fresh.
    const unreadableRun = ed.latestRun != null && lastModifiedMs == null;
    const status: PipelineStatus =
      cadences.length === 0 || unreadableRun
        ? "unknown"
        : computePipelineStatus({
            scheduledCadences: cadences,
            perCadence: [
              {
                cadence: cadences[0] ?? "other",
                at: lastModifiedMs == null ? null : new Date(lastModifiedMs).toISOString(),
                status: ed.latestRun?.status ?? "pending",
              },
            ],
            inFlight: null,
            enabled: ed.enabled !== false,
            nowMs,
          }).status;
    return {
      id: `archive:${ed.id}`,
      wave: "archive" as const,
      label: ed.name,
      entityId: ed.id,
      cadences: [cadenceMinutes != null ? cadenceMinutesToLabel(cadenceMinutes) : ed.cadence],
      rowCount: ed.latestRun?.rowCount ?? undefined,
      status,
      dependsOnIds: [],
      active: ed.enabled !== false,
    };
  });
}

// ─── Entry point ────────────────────────────────────────────────────────────

// SIM-1350: diamond re-enabled. Order matches the data-flow direction so
// the DAG renders left-to-right with diamond between gold and archive.
const VISIBLE_WAVE_ORDER: Wave[] = [
  "connector",
  "bronze-silver-latest",
  "silver",
  "gold",
  "diamond",
  "archive",
];

/**
 * Topological plus alphabetical sort within one wave (SIM-1350). Each `dependsOnIds` pointing at
 * another node in the SAME wave is a directed edge; Kahn's algorithm emits topological order with
 * alphabetical tie-breaks, and same-wave edges that would form a cycle are ignored defensively.
 * Producers render above consumers; nodes with no intra-wave dependency sort by name.
 */
interface IntraWaveGraph {
  byId: Map<string, WaveNode>;
  inDeg: Map<string, number>;
  adj: Map<string, string[]>;
}

function buildIntraWaveGraph(nodes: WaveNode[]): IntraWaveGraph {
  const ids = new Set(nodes.map((n) => n.id));
  const byId = new Map(nodes.map((n) => [n.id, n] as const));
  const inDeg = new Map<string, number>();
  const adj = new Map<string, string[]>();
  for (const n of nodes) {
    inDeg.set(n.id, 0);
    adj.set(n.id, []);
  }
  for (const n of nodes) {
    for (const dep of n.dependsOnIds) {
      if (!ids.has(dep) || dep === n.id) continue;
      (adj.get(dep) as string[]).push(n.id);
      inDeg.set(n.id, (inDeg.get(n.id) ?? 0) + 1);
    }
  }
  return { byId, inDeg, adj };
}

function insertAlphabetically(ready: string[], id: string, byId: Map<string, WaveNode>): void {
  const label = byId.get(id)?.label ?? "";
  const idx = ready.findIndex((r) => (byId.get(r)?.label ?? "").localeCompare(label) > 0);
  if (idx === -1) ready.push(id);
  else ready.splice(idx, 0, id);
}

function sortIntraWaveTopoAlpha(nodes: WaveNode[]): WaveNode[] {
  if (nodes.length < 2) return nodes;
  const { byId, inDeg, adj } = buildIntraWaveGraph(nodes);
  const ready: string[] = [];
  for (const [id, d] of inDeg) if (d === 0) ready.push(id);
  ready.sort((a, b) => (byId.get(a)?.label ?? "").localeCompare(byId.get(b)?.label ?? ""));

  const out: WaveNode[] = [];
  while (ready.length > 0) {
    const id = ready.shift() as string;
    out.push(byId.get(id) as WaveNode);
    for (const next of adj.get(id) ?? []) {
      const remaining = (inDeg.get(next) ?? 0) - 1;
      inDeg.set(next, remaining);
      if (remaining === 0) insertAlphabetically(ready, next, byId);
    }
  }

  // Cycle fallback: append remaining nodes alphabetically.
  if (out.length < nodes.length) {
    const remaining = nodes.filter((n) => !out.some((o) => o.id === n.id));
    remaining.sort((a, b) => a.label.localeCompare(b.label));
    out.push(...remaining);
  }
  return out;
}

export function buildPipelineGraph(input: BuildPipelineGraphInput): PipelineGraph {
  const { snapshot, projectDetail, runStatus, now } = input;
  const cadenceByTableBase = buildTableCadenceMap(projectDetail.connectors);
  const runIndex = indexRunStatus(runStatus);
  const snapshotIndex = indexSnapshotTables(snapshot);
  const outputLookup = buildOutputLookup(projectDetail.transformDefinitions, snapshot);

  const waves: Record<Wave, WaveNode[]> = {
    ...EMPTY_WAVES,
    connector: buildConnectorNodes(projectDetail.connectors),
    "bronze-silver-latest": buildBronzeSilverLatestNodes(
      snapshot,
      cadenceByTableBase,
      projectDetail.connectors,
      runIndex,
    ),
    silver: sortIntraWaveTopoAlpha(
      buildTransformDefinitionNodes(
        projectDetail.transformDefinitions,
        snapshotIndex,
        outputLookup,
        "silver-canon",
        "silver",
        runIndex,
      ),
    ),
    gold: sortIntraWaveTopoAlpha(
      buildTransformDefinitionNodes(
        projectDetail.transformDefinitions,
        snapshotIndex,
        outputLookup,
        "gold",
        "gold",
        runIndex,
      ),
    ),
    diamond: sortIntraWaveTopoAlpha(
      buildTransformDefinitionNodes(
        projectDetail.transformDefinitions,
        snapshotIndex,
        outputLookup,
        "diamond",
        "diamond",
        runIndex,
      ),
    ),
    archive: buildArchiveNodes(projectDetail.exportDefinitions, now),
  };

  const visibleWaves = VISIBLE_WAVE_ORDER.filter((w) => waves[w].length > 0);

  return { waves, visibleWaves };
}
